import { ApiError, api, apiRequest } from './client';
import { useAuthStore } from '@/store/auth';
import { getUseHardwareHeader } from '@/hooks/useDevHardwareToggle';

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api/v1';

export type SessionState =
  | 'reserving'
  | 'starting'
  | 'active'
  | 'stopping'
  | 'completed'
  | 'failed'
  | 'cancelled';

export type LimitType = 'value' | 'energy' | 'full_balance';

export type EndReason =
  | 'user_stop'
  | 'limit_value_reached'
  | 'limit_energy_reached'
  | 'car_disconnected'
  | 'hub_error'
  | 'hub_timeout'
  | 'station_fault'
  | 'manual_unlock'
  | 'charger_timeout_no_plug'
  // "Sem plug" em segundos: não foi o cabo (KI-040 do Hub).
  | 'charger_start_unconfirmed'
  // O conector entrou em falha durante o start (carregador ou carro recusou).
  | 'charger_fault'
  | 'stop_transaction'
  | 'hub_ended';

/** Carteira escolhida pra pagar a sessão. Sem transbordo (T4/T8d). */
export type PayFrom = 'cash' | 'bonus';

export interface StartIn {
  point_id: string;
  connector_id: string;
  limit_type: LimitType;
  limit_value: string;
  /** Default 'cash'. Erros do backend: wallet_in_debt · bonus_insufficient. */
  pay_from?: PayFrom;
}

export interface SessionOut {
  id: string;
  point_id: string;
  connector_id: string;
  state: SessionState;
  limit_type: LimitType;
  limit_value: string;
  reserved_brl: string;
  tariff_version: number;
  tariff_per_kwh_brl: string;
  energy_kwh: string;
  cost_brl: string;
  started_at: string;
  ended_at: string | null;
  end_reason: EndReason | null;
  // Snapshot da última leitura de medidor (preenchido em GET /active e /{id}).
  // Permite a tela de recarga abrir já com o SoC/potência correntes, sem
  // esperar o próximo push do WebSocket. null se ainda não houve leitura.
  soc_percent?: number | null;
  power_kw?: string | null;
  // Aviso "sua recarga vai parar em breve" (saldo acabando) ligado — mesmo
  // momento dos snapshots acima, pra tela reaberta já mostrar o banner.
  balance_stop_warning?: boolean;
  // Eletroposto da sessão + URLs de avaliação externa (PlugShare, Google Maps,
  // Carregados). Preenchidos só no GET /{id}, consumidos pelo card de NPS
  // pós-recarga em /receipt/<id>. Vazio nos demais reads.
  station_name?: string | null;
  external_review_urls?: Record<string, string>;
  /** Carteira da sessão. null = sessão anterior à separação. */
  pay_from?: PayFrom | null;
  /**
   * Quebra REAL da cobrança (recibo, T6) — só no GET /charging/{id}:
   * paid_brl = o que saiu da carteira; pending_brl = o que ficou devendo
   * (recarga laranja+ que passou do saldo).
   */
  paid_brl?: string | null;
  pending_brl?: string | null;
  /**
   * Cross-sell do aspirador no recibo — só no GET /charging/{id}, e só quando
   * a estação tem `capabilities.vacuum` E um device habilitado. `vacuum_from_*`
   * é o bloco mais barato, resolvido pela mesma cascata da compra. Ausentes
   * (null/undefined) = sem aspirador ⇒ o card não renderiza.
   */
  station_id?: string | null;
  vacuum_from_brl?: string | null;
  vacuum_from_seconds?: number | null;
  /** Cortesia disponível: o card anuncia "grátis" em vez do preço. */
  vacuum_courtesy?: boolean;
}

export const chargingApi = {
  start: (body: StartIn) => {
    // Em dev (hostname dev.* / localhost), envia X-Bow-Use-Hardware quando o
    // user marcou o toggle "Hardware real". Backend honra só fora de prod.
    const useHw = getUseHardwareHeader();
    const headers = useHw ? { 'X-Bow-Use-Hardware': useHw } : undefined;
    return apiRequest<SessionOut>('/charging/start', {
      method: 'POST',
      body,
      headers
    });
  },
  stop: (id: string) => api.post<SessionOut>(`/charging/${id}/stop`),
  active: () => api.get<SessionOut | null>('/charging/active'),
  get: (id: string) => api.get<SessionOut>(`/charging/${id}`),
  downloadReceiptPdf: async (id: string): Promise<Blob> => {
    // Endpoint retorna PDF binário, então não passa pelo apiRequest (que
    // assume JSON). Reaproveita o token do auth store + trata 401/4xx
    // de forma consistente com ApiError.
    const token = useAuthStore.getState().token;
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    let res: Response;
    try {
      res = await fetch(`${API_BASE}/charging/${id}/receipt.pdf`, { headers });
    } catch {
      // Sem resposta legível (rede, ou erro do servidor sem CORS): antes o
      // cliente via o "Failed to fetch" cru do browser (2026-09-23).
      throw new ApiError(
        0,
        'network_error',
        'Não foi possível baixar o recibo. Verifique sua internet e tente de novo.'
      );
    }
    if (!res.ok) {
      let code = 'receipt_failed';
      let message = res.statusText;
      try {
        const data = await res.json();
        code = data.error ?? data.code ?? data.detail?.code ?? data.detail ?? code;
        message = data.message ?? data.detail?.message ?? data.detail ?? message;
      } catch {
        // non-JSON error body
      }
      if (res.status === 401) useAuthStore.getState().logout();
      throw new ApiError(res.status, String(code), String(message));
    }
    return res.blob();
  },
  /**
   * URL assinada (5 min) do PDF, pro app nativo abrir no navegador do sistema:
   * o WebView do Capacitor não tem handler de download, então o `<a download>`
   * com `blob:` do PWA não faz nada no app instalado.
   */
  receiptDownloadUrl: async (id: string): Promise<string> => {
    const { path } = await api.post<{ path: string; expires_in: number }>(
      `/charging/${id}/receipt-link`
    );
    return `${API_BASE}${path}`;
  }
};
